import * as React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { panelSx } from "../pages/admin/AdminShell";

export type LegalSection = { title: string; body: string[] };

// Shared layout for the Terms and Privacy pages, in the same look as the
// admin area and the entry screen: flat gradient page, solid dark panel.
export default function LegalPage(props: {
  title: string;
  updated: string;
  intro: string;
  sections: LegalSection[];
  handlePageChange: (page: string) => void;
}) {
  const { title, updated, intro, sections, handlePageChange } = props;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(160deg, #362654 0%, #2A2740 35%, #33384A 70%, #3A3F52 100%)",
        px: { xs: 2, sm: 3, md: 5 },
        pt: { xs: 9, sm: 9, md: 7 },
        pb: { xs: 4, sm: 5, md: 6 },
      }}
    >
      <Stack spacing={3} sx={{ width: "100%", maxWidth: 860, mx: "auto" }}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap rowGap={1}>
          <Box
            component="img"
            src="/logo.png"
            alt=""
            sx={{
              width: 40,
              height: 40,
              objectFit: "contain",
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.92)",
              p: 0.55,
            }}
          />
          <Typography
            sx={{
              flex: 1,
              color: "#D1D5DB",
              fontWeight: 800,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              fontSize: 11,
            }}
          >
            Stellaris Mission Control
          </Typography>

          {/* "Welcome" sends the app back to "/", where it picks the right
              screen for whoever is (or isn't) signed in. */}
          <Button
            onClick={() => handlePageChange("Welcome")}
            startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 17 }} />}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              fontSize: 13,
              borderRadius: 999,
              px: 1.75,
              color: "#D1D5DB",
              bgcolor: "#2B2E40",
              border: "1px solid #3E4258",
              "&:hover": { bgcolor: "#363A4F" },
            }}
          >
            Back
          </Button>
        </Stack>

        <Stack spacing={0.75}>
          <Typography component="h1" sx={{ m: 0, color: "#FFFFFF", fontWeight: 900, fontSize: { xs: 24, sm: 28 } }}>
            {title}
          </Typography>
          <Typography sx={{ color: "rgba(226,232,240,0.6)", fontSize: 12.5 }}>{updated}</Typography>
        </Stack>

        <Box sx={panelSx}>
          <Typography sx={{ color: "#FFFFFF", fontSize: 14.5, lineHeight: 1.65 }}>{intro}</Typography>
        </Box>

        <Box sx={panelSx}>
          <Stack spacing={2.5}>
            {sections.map((section, i) => (
              <Stack
                key={section.title}
                component="section"
                spacing={1}
                sx={{ pt: i === 0 ? 0 : 2.5, borderTop: i === 0 ? "none" : "1px solid rgba(255,255,255,0.08)" }}
              >
                <Typography component="h2" sx={{ m: 0, color: "#E9D5FF", fontWeight: 800, fontSize: 15.5 }}>
                  {section.title}
                </Typography>
                {section.body.map((line, j) => (
                  <Typography key={j} sx={{ color: "rgba(226,232,240,0.78)", fontSize: 13.5, lineHeight: 1.65 }}>
                    {line}
                  </Typography>
                ))}
              </Stack>
            ))}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
